import { useEffect, useMemo, useState } from "react";

import { FindPersonContactMode, usesFindPersonWaterfall } from "@signalist/shared-types";

/** The list fields the choice is read from. */
interface ContactModeList {
	sourceType?: string | null;
	findPersonContactMode?: FindPersonContactMode | null;
}

/** The stored mode, or the default for a list that never set one. */
const storedMode = (list?: ContactModeList): FindPersonContactMode =>
	list?.findPersonContactMode === FindPersonContactMode.NEW_PEOPLE_ONLY
		? FindPersonContactMode.NEW_PEOPLE_ONLY
		: FindPersonContactMode.CONTACTS_FIRST;

/**
 * The Find person step's "Use my contacts first" / "Find new people only" choice (PREC-3899):
 * offered exactly where the list finds people at its companies (`usesFindPersonWaterfall`, the
 * predicate the API reads too), seeded from the list, and saved only where it is offered.
 */
export function useFindPersonContactMode(
	list: ContactModeList | undefined,
	scrapeLevel: string | null | undefined,
	entityType: string | null | undefined
) {
	const [mode, setMode] = useState<FindPersonContactMode>(storedMode(list));

	useEffect(() => {
		if (list) {
			setMode(storedMode(list));
		}
	}, [list]);

	const visible = useMemo(
		() =>
			!!list && usesFindPersonWaterfall({ sourceType: list.sourceType, entityType, scrapeLevel }),
		[list, entityType, scrapeLevel]
	);

	return {
		visible,
		mode,
		setMode,
		/** What the step's save adds to the list update: the mode where it is offered, else nothing. */
		saveFields: visible ? { findPersonContactMode: mode } : {},
	};
}
